import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, CheckCircle, Mail, Percent } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

interface PendingRequest {
  id: string;
  email: string;
  full_name: string;
  message: string | null;
  requested_at: string;
}

interface PartnerApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: PendingRequest | null;
  onApprovalComplete: () => void;
}

const PartnerApprovalModal: React.FC<PartnerApprovalModalProps> = ({
  isOpen,
  onClose,
  request,
  onApprovalComplete,
}) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [performanceFeePct, setPerformanceFeePct] = useState('20');

  const handleApprove = async () => {
    if (!request) return;

    const feeValue = parseFloat(performanceFeePct);
    if (isNaN(feeValue) || feeValue < 0 || feeValue > 100) {
      toast({
        title: "Invalid Performance Fee",
        description: "Please enter a valid percentage between 0 and 100.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Call the edge function - credentials will be sent to manager's email
      const response = await fetch(
        `https://qjgfpmpruszbgvggqdmg.supabase.co/functions/v1/process-partner-approval`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: request.email,
            action: 'approve',
            managerEmail: user?.email || 'cking@del-phi.com',
            performanceFeePct: feeValue,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to approve partner');
      }

      toast({
        title: "Partner Approved",
        description: `Account created for ${request.full_name}. Login credentials have been sent to your email.`,
      });

      onApprovalComplete();
      onClose();
    } catch (error: any) {
      console.error('Error approving partner:', error);
      toast({
        title: "Approval Failed",
        description: error.message || "An error occurred while approving the partner.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!request) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-delphi-dark border-delphi-gray/30">
        <DialogHeader>
          <DialogTitle className="text-xl text-delphi-light">
            Approve Partner Request
          </DialogTitle>
          <DialogDescription className="text-delphi-light/60">
            Confirm approval for this partner application.
          </DialogDescription>
        </DialogHeader>

        <div className="py-6">
          {/* Partner Info */}
          <div className="bg-delphi-dark/50 border border-delphi-gray/20 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-medium text-delphi-light mb-2">{request.full_name}</h3>
            <p className="text-delphi-light/60">{request.email}</p>
            {request.message && (
              <p className="text-delphi-light/50 text-sm mt-3 italic border-t border-delphi-gray/20 pt-3">
                "{request.message}"
              </p>
            )}
            <p className="text-delphi-light/40 text-xs mt-3">
              Requested: {new Date(request.requested_at).toLocaleString()}
            </p>
          </div>

          {/* Performance Fee */}
          <div className="bg-delphi-dark/50 border border-delphi-gray/20 rounded-lg p-4 mb-6">
            <Label htmlFor="performanceFee" className="text-delphi-light flex items-center gap-2 mb-3">
              <Percent size={16} className="text-delphi-red" />
              Performance Fee Percentage *
            </Label>
            <div className="relative">
              <Input
                id="performanceFee"
                type="number"
                min="0"
                max="100"
                step="0.5"
                placeholder="20"
                value={performanceFeePct}
                onChange={(e) => setPerformanceFeePct(e.target.value)}
                className="bg-delphi-dark text-delphi-light border-delphi-gray/40 pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-delphi-light/50">%</span>
            </div>
            <p className="text-delphi-light/50 text-xs mt-2">
              This fee will be applied to the partner's profits during trade calculations.
            </p>
          </div>

          {/* What happens next */}
          <div className="bg-delphi-red/10 border border-delphi-red/30 rounded-lg p-4">
            <h4 className="text-sm font-medium text-delphi-light flex items-center gap-2 mb-3">
              <Mail size={16} className="text-delphi-red" />
              What happens when you approve:
            </h4>
            <ol className="text-sm text-delphi-light/70 space-y-2 list-decimal list-inside">
              <li>A partner account will be created with a {performanceFeePct || '20'}% performance fee.</li>
              <li>Login credentials will be sent to <strong className="text-delphi-light">your email</strong>.</li>
              <li>You contact the partner and share their credentials.</li>
              <li>Partner logs in and completes their onboarding profile.</li>
              <li>You do final review and activate their account.</li>
            </ol>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="text-delphi-light border-delphi-gray/40"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            className="bg-green-600 hover:bg-green-700"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Approving...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Approve & Send Credentials
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PartnerApprovalModal;